import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs'
import {PenaltyModel} from '../models/penalty.model';
import {environment} from '../../environments/environment';
import {map} from "rxjs/operators";
import {BrakingBehaviorDto} from "../dashboard/drives/drive-chart-braking/BrakingBehaviorDto";
import {AccelerationProfileDto} from "../dashboard/drives/drive-chart-acceleration/AccelerationProfileDto";
import {ElevationProfileDto} from "../dashboard/drives/elevation-profile/ElevationProfileDto";
import {
  accelerationProfileData,
  brakingBehaviorData,
  scorecardData
} from "../dashboard/drives/dummy-data"
import {DriveDetails, DriveScorecard, ScorecardMapFeaturesDto, TripScoreType, TripScoreTypeModelDto} from "../dashboard/scorecard/DriveScorecard"
import {DriveEventDto} from "../dashboard/drives/drive-events/DriveEventDto";

@Injectable()
export class DriveDataSource {

  constructor(
    private http: HttpClient
  ) {}

  getTrackPenalties(sessionKey: string): Observable<PenaltyModel[]> {
    return this.http.get<PenaltyModel[]>(environment.backendApi + 'api/TrackLogs/GetTrackPenalties/' + sessionKey);
  }

  getEvents(sessionKey: string): Observable<DriveEventDto[]> {
    return this.http.get<DriveEventDto[]>(environment.backendApi + `api/Scorecard/Events/${sessionKey}`);
  }

  getDetails(sessionKey: string): Observable<DriveDetails> {
    return this.http.get<DriveDetails>(environment.backendApi + `api/Scorecard/Details/${sessionKey}`);
  }

  getFeatures(sessionKey: string, intervalSeconds: number): Observable<ScorecardMapFeaturesDto> {
    return this.http.get<ScorecardMapFeaturesDto>(environment.backendApi + `api/Scorecard/MapFeatures/${sessionKey}/${intervalSeconds}`);
  }

  getDummyScorecardData(sessionKey: string): Observable<DriveScorecard> {
    return of(scorecardData as any);
  }

  getScorecardData(sessionKey: string, intervalSeconds?: number): Observable<DriveScorecard> {
    const details = this.getDetails(sessionKey);
    const events = this.getEvents(sessionKey);
    const features = this.getFeatures(sessionKey, intervalSeconds ? intervalSeconds : 5);
    const tripScoreTypeModel = this.getTripScoreType(sessionKey);

    return forkJoin([events, details, features, tripScoreTypeModel]).pipe(
      map(([events, details, features, tripScoreType]) => (
        {
          events: events,
          details: details,
          features: features,
          tripScoreType: TripScoreType[tripScoreType.tripScoreType]
        })
      )
    );
  }

  getDummyAccelerationProfileData(sessionKey: string): Observable<AccelerationProfileDto> {
    return of(accelerationProfileData as AccelerationProfileDto)
  }

  getAccelerationProfileData(sessionKey: string): Observable<AccelerationProfileDto> {
    return this.http.get<AccelerationProfileDto>(environment.backendApi + `api/Scorecard/AccelerationSummary/${sessionKey}`);
  }

  getRecentAccelerationProfileData(sessionKey: string): Observable<AccelerationProfileDto> {
    return this.http.get<AccelerationProfileDto>(environment.backendApi + `api/Scorecard/RecentAccelerationSummary/${sessionKey}`);
  }

  getTopPerformerAccelerationProfileData(): Observable<AccelerationProfileDto> {
    return this.http.get<AccelerationProfileDto>(environment.backendApi + `api/Scorecard/TopPerformerAccelerationSummary`);
  }

  getDummyBrakingBehaviorData(sessionKey: string): Observable<BrakingBehaviorDto[]> {
    return of(brakingBehaviorData as BrakingBehaviorDto[])
  }

  getBrakingBehaviorData(sessionKey: string): Observable<BrakingBehaviorDto[]> {
    return this.http.get<BrakingBehaviorDto[]>(environment.backendApi + `api/Scorecard/BrakingBehavior/${sessionKey}`);
  }

  getElevationProfileData(sessionKey: string): Observable<ElevationProfileDto[]> {
    return this.http.get<ElevationProfileDto[]>(environment.backendApi + `api/Scorecard/ElevationProfile/${sessionKey}`);
  }

  getTripScoreType(sessionKey: string): Observable<TripScoreTypeModelDto> {
    return this.http.get<TripScoreTypeModelDto>(environment.backendApi + `api/Scorecard/TripScoreType/${sessionKey}`);
  }

  exportSessionData(sessionKey: string): Observable<Blob> {
    return this.http.get(
      `${environment.backendApi}api/TrackLogs/ExportSessionData/${sessionKey}`,
      {
        headers: {'Content-Type': 'text/csv'},
        responseType: 'blob'
      });
  }

  deleteSession(sessionKey: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.backendApi}api/TrackLogs/DeleteSession/${sessionKey}`
    );
  }
}
